import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Sun, Moon, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../hooks/useDarkMode';
import { useHourFormat } from '../../hooks/useHourFormat';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

export function SettingsButton({ variant = 'default' }: { variant?: 'default' | 'room' }) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useDarkMode();
  const { hourFormat, setHourFormat } = useHourFormat();
  const [isOpen, setIsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsLanguageOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
  };

  const isRoom = variant === 'room';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className={`flex items-center justify-center rounded-lg p-2 transition-colors ${
          isRoom
            ? 'h-[38px] w-[38px] border border-room-border bg-room-surface-2 text-room-text-dim hover:bg-room-surface-3'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
        }`}
        title={t('header.settings')}
        aria-expanded={isOpen}
      >
        <Settings size={20} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute right-0 z-50 mt-2 w-[15.5rem] max-h-[min(70vh,calc(100dvh-5rem))] max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-lg shadow-lg ${
              isRoom
                ? 'border border-room-border bg-room-surface-2'
                : 'border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
            }`}
          >
            <div className={`border-b px-3 py-3 ${isRoom ? 'border-room-border' : 'border-gray-200 dark:border-gray-700'}`}>
              <div className={`mb-2 text-xs font-semibold uppercase tracking-wider ${isRoom ? 'text-room-text-faint' : 'text-gray-500 dark:text-gray-400'}`}>
                {t('header.theme')}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => handleThemeChange('light')}
                  className={`flex min-w-0 flex-col items-center gap-1 rounded-lg px-2 py-2 text-xs font-medium leading-tight transition-all ${
                    theme === 'light'
                      ? isRoom
                        ? 'bg-room-accent-soft text-room-accent'
                        : 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : isRoom
                        ? 'text-room-text-dim hover:bg-room-surface-3'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <Sun size={16} className="shrink-0" />
                  <span className="text-center">{t('header.light')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleThemeChange('dark')}
                  className={`flex min-w-0 flex-col items-center gap-1 rounded-lg px-2 py-2 text-xs font-medium leading-tight transition-all ${
                    theme === 'dark'
                      ? isRoom
                        ? 'bg-room-accent-soft text-room-accent'
                        : 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : isRoom
                        ? 'text-room-text-dim hover:bg-room-surface-3'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <Moon size={16} className="shrink-0" />
                  <span className="text-center">{t('header.dark')}</span>
                </button>
              </div>
            </div>

            <div className={`border-b px-3 py-3 ${isRoom ? 'border-room-border' : 'border-gray-200 dark:border-gray-700'}`}>
              <div className={`mb-2 text-xs font-semibold uppercase tracking-wider ${isRoom ? 'text-room-text-faint' : 'text-gray-500 dark:text-gray-400'}`}>
                {t('header.hours')}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => setHourFormat('24h')}
                  className={`flex min-w-0 flex-col items-center gap-1 rounded-lg px-2 py-2 text-xs font-medium leading-tight transition-all ${
                    hourFormat === '24h'
                      ? isRoom
                        ? 'bg-room-accent-soft text-room-accent'
                        : 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : isRoom
                        ? 'text-room-text-dim hover:bg-room-surface-3'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-center font-mono">{t('header.hours24')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setHourFormat('12h')}
                  className={`flex min-w-0 flex-col items-center gap-1 rounded-lg px-2 py-2 text-xs font-medium leading-tight transition-all ${
                    hourFormat === '12h'
                      ? isRoom
                        ? 'bg-room-accent-soft text-room-accent'
                        : 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : isRoom
                        ? 'text-room-text-dim hover:bg-room-surface-3'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-center">{t('header.hours12')}</span>
                </button>
              </div>
            </div>

            <div className="px-3 py-3">
              <div className={`mb-2 text-xs font-semibold uppercase tracking-wider ${isRoom ? 'text-room-text-faint' : 'text-gray-500 dark:text-gray-400'}`}>
                {t('header.language')}
              </div>
              <button
                type="button"
                onClick={() => setIsLanguageOpen(open => !open)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors ${
                  isRoom
                    ? 'bg-room-surface-3 text-room-text hover:bg-room-surface-2'
                    : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600'
                }`}
                aria-expanded={isLanguageOpen}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-lg ${isRoom ? 'text-room-text-dim' : 'text-gray-700 dark:text-gray-300'}`}>
                    {currentLanguage.flag}
                  </span>
                  <span className={`text-sm font-medium ${isRoom ? 'text-room-text' : 'text-gray-700 dark:text-gray-300'}`}>
                    {currentLanguage.name}
                  </span>
                </div>
                <ChevronDown
                  size={16}
                  className={`shrink-0 transition-transform ${isLanguageOpen ? 'rotate-180' : ''} ${isRoom ? 'text-room-text-faint' : 'text-gray-500 dark:text-gray-400'}`}
                />
              </button>

              <AnimatePresence initial={false}>
                {isLanguageOpen ? (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    <div
                      className={`mt-1 overflow-hidden rounded-lg border ${
                        isRoom
                          ? 'border-room-border bg-room-surface-3'
                          : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40'
                      }`}
                    >
                      {languages.map(lang => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                            i18n.language === lang.code
                              ? isRoom
                                ? 'bg-room-accent-soft text-room-accent'
                                : 'bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                              : isRoom
                                ? 'text-room-text-dim hover:bg-room-surface-2'
                                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span className="flex-1 text-sm font-medium">{lang.name}</span>
                          {i18n.language === lang.code ? (
                            <span className={isRoom ? 'text-room-accent' : 'text-primary-600 dark:text-primary-400'}>✓</span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
